/**
 * Known-benign browser noise that should never be reported as a frontend error.
 *
 * These messages are dispatched as global `error` events by the browser but do
 * not represent actual application faults:
 *
 * - "ResizeObserver loop …" — the browser deferred some ResizeObserver
 *   notifications to the next frame; the observation still completes correctly.
 *   Fires with an empty filename and lineno 0, and is not shown as an uncaught
 *   error. Every major error tracker ignores it by default.
 */
const IGNORED_ERROR_PATTERNS: readonly RegExp[] = [/ResizeObserver loop/i];

/**
 * Script origins that are not ours, so an error raised inside them is not a
 * fault in this application — matched against the URL of the frame that threw.
 *
 * Shared by both error sinks: Sentry takes it as `denyUrls`, which drops an
 * event when ANY stack frame comes from one of these, and the PostHog error
 * net checks the `filename` of a `window` error event against it.
 *
 * - `iabjs://` — the bridge scheme of the Facebook and Instagram Android
 *   in-app browsers. They inject a navigation-performance logger into every
 *   page, and when the WebView tears its native bridge down during unload the
 *   logger's `postMessage` fails with "Java object is gone". Nothing of ours is
 *   on that stack (Sentry issue 7778675124).
 * - `chrome-extension://`, `moz-extension://`, `safari-extension://`,
 *   `safari-web-extension://` — content scripts a visitor's own browser
 *   extensions run inside our page (password managers, ad blockers,
 *   translators). Their failures are theirs to fix and we cannot reproduce
 *   them.
 */
export const THIRD_PARTY_SCRIPT_URLS: readonly RegExp[] = [
    /^iabjs:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari(-web)?-extension:\/\//i,
];

/**
 * Errors raised by third-party scripts that cannot be told apart by URL,
 * because the script is injected INLINE into our document, so the throwing
 * frame reports our own page URL (`https://tourium.com/:1`) — matched against
 * the error message instead.
 *
 * Shared by both error sinks: Sentry takes it as `ignoreErrors` (tested
 * against the exception value and `Type: value`), the PostHog error net
 * against the `message` of a `window` error event.
 *
 * - `window.webkit.messageHandlers` — the iOS counterpart of the `iabjs://`
 *   bridge above. The Facebook in-app browser on iOS injects a largest-
 *   contentful-paint logger (`processLargestContentfulPaintEvent` →
 *   `sendDataToNative`) that posts to the WebKit native bridge, and on some
 *   builds the bridge is not there. We never touch `webkit.messageHandlers`
 *   ourselves (Sentry issue 7781259776).
 */
export const THIRD_PARTY_SCRIPT_MESSAGES: readonly RegExp[] = [/evaluating 'window\.webkit\.messageHandlers/i];

/**
 * Determine whether an uncaught error is worth reporting, or whether it is
 * known-benign browser noise (by message), a third-party script's own fault
 * (by the URL of the script that threw), or an inline third-party script's
 * own fault (by message, since its URL is ours) that should be dropped from
 * the error net. `filename` is the `ErrorEvent.filename` when the caller has
 * one.
 */
export const isReportableError = (message: string, filename?: string): boolean => {
    if (IGNORED_ERROR_PATTERNS.some((pattern) => pattern.test(message))) {
        return false;
    }

    if (THIRD_PARTY_SCRIPT_MESSAGES.some((pattern) => pattern.test(message))) {
        return false;
    }

    if (filename && THIRD_PARTY_SCRIPT_URLS.some((pattern) => pattern.test(filename))) {
        return false;
    }

    return true;
};
